import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import ProcesosApi from "api/procesosApi";

const DescripcionGeneral = ({ procesoId, propuestaId = null, setError, tipo_propuesta = null }) => {
  const [descripcion, setDescripcion] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempDescripcion, setTempDescripcion] = useState("");
  const [propuestaActual, setPropuestaActual] = useState(null);

  const cargarDescripcion = useCallback(async () => {
    if (!procesoId) return;

    try {
      const response = await ProcesosApi.obtenerPropuestas(procesoId, tipo_propuesta);
      let propuestaTarget = null;
      // Si tenemos un propuestaId, buscar esa propuesta específica
      if (propuestaId) {
        propuestaTarget = response.data?.find((p) => p.id === propuestaId);
      } else {
        propuestaTarget = response.data[0];
      }
      setPropuestaActual(propuestaTarget);
      if (propuestaTarget?.descripcion_general) {
        setDescripcion(propuestaTarget.descripcion_general);
        setTempDescripcion(propuestaTarget.descripcion_general);
      } else {
        setDescripcion("");
        setTempDescripcion("");
      }
    } catch (error) {
      console.error("Error al cargar descripción:", error);
      setError(obtenerMensajeError(error, "Error al cargar la descripción general"));
    }
  }, [procesoId, propuestaId, setError]);

  const handleGuardar = async () => {
    try {
      setIsSaving(true);

      if (!propuestaActual?.id) {
        throw new Error("No hay una propuesta activa");
      }

      await ProcesosApi.actualizarPropuesta(propuestaActual.id, {
        proceso: procesoId,
        descripcion_general: tempDescripcion,
        activa: true,
      });

      setDescripcion(tempDescripcion);
      setIsEditing(false);
      await cargarDescripcion(); // Recargar para asegurar sincronización
    } catch (error) {
      console.error("Error al guardar descripción:", error);
      setError(obtenerMensajeError(error, "Error al guardar la descripción general"));
      setTempDescripcion(descripcion);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (procesoId) {
      cargarDescripcion();
    }
  }, [procesoId, cargarDescripcion]);

  const handleEditar = () => {
    setTempDescripcion(descripcion);
    setIsEditing(true);
  };

  const handleCancelar = () => {
    setTempDescripcion(descripcion);
    setIsEditing(false);
  };

  return (
    <SuiBox mt={3} pt={2} borderTop={1} borderColor="grey.300">
      <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SuiTypography variant="h6" fontWeight="medium">
          Descripción General
        </SuiTypography>
        <Box display="flex" gap={1}>
          <Tooltip title="Recargar descripción">
            <IconButton onClick={cargarDescripcion} disabled={isEditing || isSaving} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {descripcion && !isEditing ? (
            <SuiButton
              variant="outlined"
              buttonColor="info"
              onClick={handleEditar}
              disabled={isSaving}
              size="small"
            >
              Editar
            </SuiButton>
          ) : isEditing ? (
            <>
              <SuiButton
                variant="outlined"
                buttonColor="error"
                onClick={handleCancelar}
                disabled={isSaving}
                size="small"
              >
                Cancelar
              </SuiButton>
              <SuiButton
                variant="gradient"
                buttonColor="info"
                onClick={handleGuardar}
                disabled={isSaving}
                startIcon={<SaveIcon />}
                size="small"
              >
                {isSaving ? "Guardando..." : "Guardar"}
              </SuiButton>
            </>
          ) : null}
        </Box>
      </SuiBox>

      <SuiBox>
        {isEditing ? (
          <SuiBox width="90%" mx="auto">
            <textarea
              value={tempDescripcion}
              onChange={(e) => setTempDescripcion(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "0.875rem",
                lineHeight: "1.6",
                fontFamily: "inherit",
                resize: "none",
                backgroundColor: "#fff",
                whiteSpace: "pre-wrap",
                height: "auto",
                fontWeight: "400",
              }}
              rows={tempDescripcion.split("\n").length} // Se ajusta al número de líneas
            />
          </SuiBox>
        ) : (
          <SuiBox
            bgcolor="grey.100"
            borderRadius={1}
            width="90%"
            mx="auto"
            p={2}
            sx={{
              whiteSpace: "pre-wrap",
              fontSize: "0.875rem",
              lineHeight: "1.6",
              color: "text.secondary",
            }}
          >
            {descripcion || "No hay descripción disponible. Haga clic en actualizar para recargar."}
          </SuiBox>
        )}
      </SuiBox>
    </SuiBox>
  );
};

DescripcionGeneral.propTypes = {
  procesoId: PropTypes.number.isRequired,
  propuestaId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  setError: PropTypes.func.isRequired,
  tipo_propuesta: PropTypes.string,
};

export default DescripcionGeneral;
